import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import Button from 'components/Button';
import Toast from 'components/Toast';
import { clientAuthorizedPatch } from 'utils/fetchUtils';
import { API_ROUTES, ROUTES } from 'utils/constants';
import styles from './ChangePassword.module.scss';

function ChangePassword() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFieldError(null);

      if (newPassword.length < 8) {
        setFieldError('New password must be at least 8 characters.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setFieldError('New password and confirmation do not match.');
        return;
      }

      setSubmitting(true);
      try {
        await clientAuthorizedPatch<{ message: string }>(
          API_ROUTES.AUTH.PASSWORD,
          {
            currentPassword,
            newPassword,
          },
        );
        setToast({
          message: 'Password updated successfully.',
          type: 'success',
        });
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        window.setTimeout(() => {
          navigate(ROUTES.USER.PROFILE);
        }, 1200);
      } catch (err) {
        const msg =
          err instanceof Error ? err.message : 'Could not update password.';
        setToast({ message: msg, type: 'error' });
      } finally {
        setSubmitting(false);
      }
    },
    [confirmPassword, currentPassword, navigate, newPassword],
  );

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>Change password</h1>
        <p className={styles.subtitle}>
          Enter your current password, then choose a new one. You will stay
          signed in on this device.
        </p>
      </div>

      <div className={styles.card}>
        <form className={styles.form} onSubmit={(e) => void handleSubmit(e)}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="current-password">
              Current password
            </label>
            <input
              id="current-password"
              className={styles.input}
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={submitting}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="new-password">
              New password
            </label>
            <input
              id="new-password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
            />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="confirm-password">
              Confirm new password
            </label>
            <input
              id="confirm-password"
              className={styles.input}
              type="password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              disabled={submitting}
            />
          </div>
          {fieldError ? (
            <p className={styles.errorText} role="alert">
              {fieldError}
            </p>
          ) : null}
          <div className={styles.actions}>
            <Button
              type="submit"
              disabled={submitting}
              className={styles.submitButton}
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className={styles.spinner} aria-hidden />
                  Updating…
                </>
              ) : (
                'Update password'
              )}
            </Button>
            <button
              type="button"
              className={styles.cancelLink}
              onClick={() => navigate(ROUTES.USER.PROFILE)}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {toast ? (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

export default ChangePassword;
